import React, { RefObject, useCallback, useMemo, useState } from 'react';
import { useResourceStatsFiles } from 'new/hooks/adapters/useResourceStatsFiles';
import { useDrawerContext } from 'new/components/drawer/useDrawerContext';
import { FileItemsList } from 'new/elements/FileItemsList';
import { FILE_LIST_MODE_TYPES } from 'js/constants/paginationConstants';
import { ReingestDialogContent } from 'js/components/elements/files/ReingestDialogContent';
import { Dialog } from '@mui/material';
import { FileUtil } from 'new/utils/FileUtil';
import { useResource } from 'new/components/monitor/ResourceTriage/useResource';
import ReadStatusHeader from 'new/elements/ReadStats/components/ReadStatusHeader';
import { useFilteredData } from 'new/hooks/useFilteredData';
import { FileItemDetails } from 'src/new/elements/FileItemDetails';
import { useReingestFiles } from '../hooks/useReingestFiles';
import { useFileItemState } from 'new/elements/ReadStats/hooks/useFileItemState';
import { useMultipleReingestState } from 'new/elements/ReadStats/hooks/useMultipleReingestState';
import { ReingestButtonGroup } from './ReingestButtonGroup';
import { shouldHideRecordsForResource } from 'js/lib/PipelineUtils';

interface ItemFileProps {
  resourceType: string;
  resourceId: string;
  mode?: keyof typeof FILE_LIST_MODE_TYPES;
  connectionCategory: string;
  parentScrollRef?: RefObject<any>;
  height?: number;
  onFilePreviewClicked?: (props: any) => void;
  writeStatus?: string;
  data?: any;
  requestFiles: (page: number) => void;
  onChangeSort: (e: any) => void;
  onChangeStatus: (e: any) => void;
  onSearchByNameChange: (name: string) => void;
  sort: string;
  status: string;
  sortByItems: any[];
  filterByStatus: any[];
  isAdaptiveFlow?: boolean;
}

export const FileItems = (props: ItemFileProps) => {
  const {
    resourceId,
    resourceType,
    connectionCategory,
    mode,
    parentScrollRef,
    requestFiles,
    onChangeSort,
    onChangeStatus,
    onSearchByNameChange,
    sort,
    status,
    sortByItems,
    filterByStatus,
    isAdaptiveFlow = false,
  } = props;

  const { files, addFile, removeFile, clearFiles } = useFileItemState();
  const {
    showReingestModal,
    isMultipleReingestActive,
    openReingestModal,
    onCloseReingestModal,
    resetMultipleReingestFile,
    enableMultipleReingestFile,
  } = useMultipleReingestState();

  const [expandedRows, setExpandedRows] = useState<string[]>([]);
  const { drawerContentRef } = useDrawerContext();
  const { resource } = useResource(resourceType, resourceId);
  const { items, meta, loading } = useResourceStatsFiles(resourceType, resourceId);
  const { filteredData, onSearchChange } = useFilteredData(items, 'name');

  const isModeRead = mode === FILE_LIST_MODE_TYPES.MODE_READ;
  const isModeWrite = mode === FILE_LIST_MODE_TYPES.MODE_WRITE;
  const shouldEnableFileDetails = isModeRead || isModeWrite;

  const { handleReingest } = useReingestFiles({
    resourceId,
    resource,
    sort,
    status,
  });

  const loadMore = useCallback((page: number) => requestFiles(page), [requestFiles]);

  const resetState = () => resetMultipleReingestFile(() => clearFiles());

  const onConfirmReingest = () => {
    onCloseReingestModal();
    const data = Array.from(files.values());

    handleReingest(data, {
      onSuccess: () => {
        resetState();
        /**
         * there is a delay in the reingestion process on the API side,
         * we need to wait for the files to be updated
         */
        setTimeout(() => {
          requestFiles(1);
        }, 600);
      },
    });
  };

  const isAnyFileEligibleForReingestion = useMemo(
    () =>
      items.some((item) =>
        FileUtil.isReingestAllowed(item.ingestionStatus, connectionCategory, mode, isAdaptiveFlow),
      ),
    [items, connectionCategory, mode],
  );

  const showConfirmReingestModal = files.size > 0 && showReingestModal;

  const filteredDataExcludingQueuedReingest = isMultipleReingestActive
    ? filteredData.filter((item) => item.ingestionStatus !== 'QUEUED_REINGEST')
    : filteredData;

  const columnsOptions = useMemo(() => {
    return {
      shouldHideRecords: shouldHideRecordsForResource(resource),
    };
  }, [resource]);

  const handleSearchChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    onSearchByNameChange(e.target.value);
  };

  return (
    <div aria-label="file-items">
      <ReadStatusHeader
        sort={sort}
        status={status}
        mode={mode}
        sortByItems={sortByItems}
        filterByStatus={filterByStatus}
        isAdaptiveFlow={isAdaptiveFlow}
        onChangeSort={onChangeSort}
        onChangeStatus={onChangeStatus}
        onSearchChange={handleSearchChange}
        rightContent={
          isAnyFileEligibleForReingestion ? (
            <ReingestButtonGroup
              size={files.size}
              onCancel={resetState}
              onConfirm={isMultipleReingestActive ? openReingestModal : enableMultipleReingestFile}
              isMultipleReingestActive={isMultipleReingestActive}
              disabled={isMultipleReingestActive && files.size === 0}
            />
          ) : null
        }
      />

      <FileItemsList
        mode={props.mode}
        pageCount={meta.pageCount || 1}
        currentPage={meta.currentPage || 1}
        connectionCategory={props.connectionCategory}
        parentScrollRef={parentScrollRef || drawerContentRef}
        loading={loading}
        loadMore={loadMore}
        emptyStateText="No files found"
        accessoryData={{
          filters: { orderby: sort, status: status === 'all' ? '' : status },
        }}
        resourceType={resourceType}
        resourceId={resourceId}
        isAdaptiveFlow={isAdaptiveFlow}
        data={props.data}
        items={filteredDataExcludingQueuedReingest}
        selectedFiles={files}
        isMultipleReingestActive={isMultipleReingestActive}
        isAnyFileEligibleForReingestion={isAnyFileEligibleForReingestion}
        addFile={addFile}
        removeFile={removeFile}
        clearFiles={resetState}
        isRowOpen={(row) => (expandedRows.includes(row?.name) ? row : null)}
        onRowClick={(row) => {
          if (expandedRows.includes(row?.name)) {
            setExpandedRows(expandedRows.filter((file) => file !== row?.name));
          } else {
            setExpandedRows([...expandedRows, row?.name]);
          }
        }}
        isRowExpandable={() => shouldEnableFileDetails}
        collapseOnRowClick={shouldEnableFileDetails}
        rowCollapsibleRender={(row) => {
          return (
            <FileItemDetails
              fileName={row.name}
              resourceType={resourceType}
              columnsOptions={columnsOptions}
              resourceId={row.dataSourceId || row.sinkId}
              parentScrollRef={parentScrollRef || drawerContentRef}
            />
          );
        }}
      />

      <Dialog
        fullWidth
        maxWidth="sm"
        onClose={onCloseReingestModal}
        open={showConfirmReingestModal}
      >
        <ReingestDialogContent
          file={[...files.values()]}
          onConfirm={onConfirmReingest}
          onClose={onCloseReingestModal}
        />
      </Dialog>
    </div>
  );
};
